import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  FaShieldAlt, FaCheckCircle, FaExclamationTriangle, FaTimesCircle,
  FaUpload, FaFileAlt, FaIdCard, FaBuilding, FaCreditCard,
  FaSpinner, FaSearch, FaCheck, FaTimes, FaClock, FaMedal
} from 'react-icons/fa';

export default function UserVerification() {
  const { API, user, setUser } = useContext(AppContext);
  
  const [riskScore, setRiskScore] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [sirenVerification, setSirenVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [verifyingSiren, setVerifyingSiren] = useState(false);
  const [siren, setSiren] = useState(user?.siren || '');

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [scoreRes, docsRes] = await Promise.all([
        axios.get(`${API}/security/risk-score/${user.id}`),
        axios.get(`${API}/security/documents/user/${user.id}`)
      ]);
      
      setRiskScore(scoreRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Error fetching verification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSirenVerification = async () => {
    if (!siren || siren.length < 9) {
      toast.error('Veuillez entrer un SIREN valide (9 chiffres)');
      return;
    }

    setVerifyingSiren(true);
    try {
      const response = await axios.get(`${API}/security/verify-siren/${siren}?user_id=${user.id}`);
      setSirenVerification(response.data);
      
      if (response.data.is_valid && response.data.is_active) {
        toast.success(`SIREN vérifié : ${response.data.company_name}`);
        // Update user's SIREN
        await axios.put(`${API}/auth/users/${user.id}`, { siren });
      } else {
        toast.error('SIREN invalide ou entreprise inactive');
      }
      
      // Refresh risk score
      const scoreRes = await axios.get(`${API}/security/risk-score/${user.id}`);
      setRiskScore(scoreRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
    } finally {
      setVerifyingSiren(false);
    }
  };

  const handleDocumentUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', user.id);
      formData.append('document_type', documentType);

      await axios.post(`${API}/security/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document téléchargé avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const getDocumentStatus = (type) => {
    const doc = documents.find(d => d.document_type === type);
    if (!doc) return null;
    return doc;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-500"><FaCheck className="mr-1" /> Vérifié</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500"><FaClock className="mr-1" /> En attente</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500"><FaTimes className="mr-1" /> Rejeté</Badge>;
      default:
        return null;
    }
  };

  const getRiskColor = (score) => {
    if (score <= 30) return 'bg-green-500';
    if (score <= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getVerificationProgress = () => {
    let progress = 0;
    
    // SIREN verification: 30%
    if (sirenVerification?.is_valid) progress += 30;
    
    // Kbis: 30%
    const kbis = getDocumentStatus('kbis');
    if (kbis?.status === 'verified') progress += 30;
    else if (kbis?.status === 'pending') progress += 15;
    
    // ID: 20%
    const id = getDocumentStatus('cni') || getDocumentStatus('passport');
    if (id?.status === 'verified') progress += 20;
    else if (id?.status === 'pending') progress += 10;
    
    // RIB: 20%
    const rib = getDocumentStatus('rib');
    if (rib?.status === 'verified') progress += 20;
    else if (rib?.status === 'pending') progress += 10;
    
    return progress;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FaSpinner className="animate-spin text-4xl text-[#635BFF]" />
      </div>
    );
  }

  const verificationProgress = getVerificationProgress();

  return (
    <div className="space-y-6">
      {/* Header with Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-[#635BFF] rounded-xl">
            <FaShieldAlt className="text-white text-2xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#0A2540]">Vérification du compte</h2>
            <p className="text-gray-500">Complétez votre profil pour obtenir le badge vérifié</p>
          </div>
        </div>
        
        {riskScore?.verification_badge && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full">
            <FaMedal className="text-green-600" />
            <span className="font-medium text-green-600">Compte Vérifié</span>
          </div>
        )}
      </div>

      {/* Verification Progress */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="font-medium">Progression de la vérification</span>
            <span className="text-[#635BFF] font-bold">{verificationProgress}%</span>
          </div>
          <Progress value={verificationProgress} className="h-3" />
          
          {verificationProgress >= 100 && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <FaCheckCircle className="text-green-600" />
              <span className="text-green-700">Félicitations ! Votre compte est entièrement vérifié.</span>
            </div>
          )}
          
          {verificationProgress < 100 && (
            <p className="mt-4 text-sm text-gray-500">
              Complétez toutes les vérifications pour obtenir le badge "Compte Vérifié" et rassurer vos partenaires.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Risk Score (if high) */}
      {riskScore && riskScore.score > 50 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <FaExclamationTriangle className="text-orange-500 text-2xl" />
              <div>
                <p className="font-medium text-orange-700">
                  Votre score de confiance est de {riskScore.score}/100
                </p>
                <p className="text-sm text-orange-600">
                  Complétez vos vérifications pour améliorer votre score et accéder à toutes les fonctionnalités.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SIREN Verification */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FaBuilding className="text-[#635BFF]" />
            Vérification SIREN
          </CardTitle>
          <CardDescription>
            Vérifiez votre numéro SIREN auprès des registres officiels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="siren">Numéro SIREN (9 chiffres)</Label>
              <Input
                id="siren"
                value={siren}
                onChange={(e) => setSiren(e.target.value.replace(/\D/g, '').slice(0, 9))}
                placeholder="123456789"
                maxLength={9}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleSirenVerification}
                disabled={verifyingSiren || siren.length !== 9}
                className="bg-[#635BFF] hover:bg-[#5246E5]"
              >
                {verifyingSiren ? (
                  <FaSpinner className="animate-spin mr-2" />
                ) : (
                  <FaSearch className="mr-2" />
                )}
                Vérifier
              </Button>
            </div>
          </div>
          
          {sirenVerification && (
            <div className={`mt-4 p-4 rounded-lg ${
              sirenVerification.is_valid && sirenVerification.is_active
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              {sirenVerification.is_valid && sirenVerification.is_active ? (
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-600 mt-1" />
                  <div>
                    <p className="font-medium text-green-700">{sirenVerification.company_name}</p>
                    <p className="text-sm text-green-600">{sirenVerification.address}</p>
                    <p className="text-sm text-green-600">
                      Activité : {sirenVerification.activity_label || sirenVerification.activity_code}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <FaTimesCircle className="text-red-600" />
                  <p className="text-red-700">
                    {sirenVerification.error || "SIREN invalide ou entreprise en liquidation"}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document Upload */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Kbis */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FaFileAlt className="text-[#635BFF]" />
              Kbis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const doc = getDocumentStatus('kbis');
              if (doc) {
                return (
                  <div>
                    {getStatusBadge(doc.status)}
                    {doc.status === 'rejected' && (
                      <p className="text-sm text-red-500 mt-2">
                        Raison : {doc.rejection_reason}
                      </p>
                    )}
                    {doc.status !== 'verified' && (
                      <div className="mt-3">
                        <input
                          type="file"
                          id="kbis-upload"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocumentUpload(e, 'kbis')}
                          className="hidden"
                        />
                        <label htmlFor="kbis-upload">
                          <Button variant="outline" size="sm" className="w-full" asChild>
                            <span className="cursor-pointer">
                              <FaUpload className="mr-2" /> Renvoyer
                            </span>
                          </Button>
                        </label>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div>
                  <p className="text-sm text-gray-500 mb-3">
                    Kbis de moins de 3 mois
                  </p>
                  <input
                    type="file"
                    id="kbis-new"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentUpload(e, 'kbis')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <label htmlFor="kbis-new">
                    <Button variant="outline" className="w-full" asChild disabled={uploading}>
                      <span className="cursor-pointer">
                        {uploading ? <FaSpinner className="animate-spin mr-2" /> : <FaUpload className="mr-2" />}
                        Télécharger
                      </span>
                    </Button>
                  </label>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* CNI/Passport */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FaIdCard className="text-[#635BFF]" />
              Pièce d'identité
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const doc = getDocumentStatus('cni') || getDocumentStatus('passport');
              if (doc) {
                return (
                  <div>
                    {getStatusBadge(doc.status)}
                    {doc.status === 'rejected' && (
                      <p className="text-sm text-red-500 mt-2">
                        Raison : {doc.rejection_reason}
                      </p>
                    )}
                    {doc.status !== 'verified' && (
                      <div className="mt-3">
                        <input
                          type="file"
                          id="cni-upload"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocumentUpload(e, 'cni')}
                          className="hidden"
                        />
                        <label htmlFor="cni-upload">
                          <Button variant="outline" size="sm" className="w-full" asChild>
                            <span className="cursor-pointer">
                              <FaUpload className="mr-2" /> Renvoyer
                            </span>
                          </Button>
                        </label>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div>
                  <p className="text-sm text-gray-500 mb-3">
                    CNI ou passeport
                  </p>
                  <input
                    type="file"
                    id="cni-new"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentUpload(e, 'cni')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <label htmlFor="cni-new">
                    <Button variant="outline" className="w-full" asChild disabled={uploading}>
                      <span className="cursor-pointer">
                        {uploading ? <FaSpinner className="animate-spin mr-2" /> : <FaUpload className="mr-2" />}
                        Télécharger
                      </span>
                    </Button>
                  </label>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* RIB */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FaCreditCard className="text-[#635BFF]" />
              RIB
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const doc = getDocumentStatus('rib');
              if (doc) {
                return (
                  <div>
                    {getStatusBadge(doc.status)}
                    {doc.status === 'rejected' && (
                      <p className="text-sm text-red-500 mt-2">
                        Raison : {doc.rejection_reason}
                      </p>
                    )}
                    {doc.status !== 'verified' && (
                      <div className="mt-3">
                        <input
                          type="file"
                          id="rib-upload"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocumentUpload(e, 'rib')}
                          className="hidden"
                        />
                        <label htmlFor="rib-upload">
                          <Button variant="outline" size="sm" className="w-full" asChild>
                            <span className="cursor-pointer">
                              <FaUpload className="mr-2" /> Renvoyer
                            </span>
                          </Button>
                        </label>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div>
                  <p className="text-sm text-gray-500 mb-3">
                    Relevé d'identité bancaire
                  </p>
                  <input
                    type="file"
                    id="rib-new"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentUpload(e, 'rib')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <label htmlFor="rib-new">
                    <Button variant="outline" className="w-full" asChild disabled={uploading}>
                      <span className="cursor-pointer">
                        {uploading ? <FaSpinner className="animate-spin mr-2" /> : <FaUpload className="mr-2" />}
                        Télécharger
                      </span>
                    </Button>
                  </label>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <FaShieldAlt className="text-blue-500 text-xl mt-1" />
            <div>
              <p className="font-medium text-blue-700">Pourquoi se faire vérifier ?</p>
              <ul className="mt-2 space-y-1 text-sm text-blue-600">
                <li>• Badge "Compte Vérifié" visible sur votre profil</li>
                <li>• Meilleure confiance des partenaires commerciaux</li>
                <li>• Accès aux fonctionnalités premium</li>
                <li>• Priorité sur les demandes de location</li>
                <li>• Protection contre les fraudes</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
